import { useEffect, useState } from 'react';
import { FiX, FiUpload, FiUser, FiCalendar, FiActivity, FiFileText, FiUserCheck } from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDateTime } from '../utils/dateFormat';

const ExamDetailModal = ({ examId, onClose, onRefresh }) => {
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (examId) {
      fetchExam();
    }
  }, [examId]);

  const fetchExam = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/exams/${examId}`);
      setExam(response.data.data);
    } catch (error) {
      toast.error('Không thể tải thông tin ca chụp');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post(`/exams/${examId}/sync`);
      toast.success('Đồng bộ ca chụp thành công');
      fetchExam();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Không thể đồng bộ ca chụp');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { label: 'Đã lên lịch', class: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Đang thực hiện', class: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Hoàn thành', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Đã hủy', class: 'bg-red-100 text-red-800' },
      synced: { label: 'Đã đồng bộ', class: 'bg-indigo-100 text-indigo-800' },
    };
    const config = statusConfig[status] || { label: status, class: 'bg-blue-100 text-blue-800' };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>{config.label}</span>;
  };

  if (!examId) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-lg font-bold text-slate-900">Chi tiết ca chụp</h2>
          <div className="flex items-center gap-2">
            {exam && exam.status !== 'synced' && (
              <button 
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white rounded text-xs font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSync} 
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Đang đồng bộ...
                  </>
                ) : (
                  <>
                    <FiUpload className="text-base" />
                    Đồng bộ
                  </>
                )}
              </button>
            )}
            <button 
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
              onClick={onClose}
            >
              <FiX className="text-xl" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
              <div className="w-7 h-7 border-2 border-slate-200 border-t-primary rounded-full animate-spin"></div>
              <p className="text-slate-500 text-sm">Đang tải thông tin ca chụp...</p>
            </div>
          ) : !exam ? (
            <div className="text-center p-8 text-red-600 text-sm">Không tìm thấy ca chụp</div>
          ) : (
            <div className="space-y-4">
              {/* Thông tin bệnh nhân */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                  <FiUser className="text-primary text-lg" />
                  <h3 className="text-sm font-semibold text-slate-900">Thông tin bệnh nhân</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-800">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-medium text-slate-600">Họ tên</span>
                    <span className="text-right flex-1 ml-3">{exam.patient_name || exam.patient_full_name || '-'}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-medium text-slate-600">Mã BN</span>
                    <span className="text-right flex-1 ml-3">{exam.patient_id || '-'}</span>
                  </div>
                  {exam.patient_birth_date && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Ngày sinh</span>
                      <span className="text-right flex-1 ml-3">{exam.patient_birth_date}</span>
                    </div>
                  )}
                  {exam.patient_sex && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Giới tính</span>
                      <span className="text-right flex-1 ml-3">
                        {exam.patient_sex === 'M'
                          ? 'Nam'
                          : exam.patient_sex === 'F'
                          ? 'Nữ'
                          : exam.patient_sex}
                      </span>
                    </div>
                  )}
                  {exam.patient_age && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Tuổi</span>
                      <span className="text-right flex-1 ml-3">{exam.patient_age}</span>
                    </div>
                  )}
                  {exam.patient_weight && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Cân nặng</span>
                      <span className="text-right flex-1 ml-3">{exam.patient_weight} kg</span>
                    </div>
                  )}
                  {exam.patient_address && (
                    <div className="md:col-span-2 flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Địa chỉ</span>
                      <span className="text-right flex-1 ml-3">{exam.patient_address}</span>
                    </div>
                  )}
                  {exam.patient_phone && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Điện thoại</span>
                      <span className="text-right flex-1 ml-3">{exam.patient_phone}</span>
                    </div>
                  )}
                  {exam.cccd && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">CCCD</span>
                      <span className="text-right flex-1 ml-3">{exam.cccd}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Thông tin ca chụp */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                  <FiActivity className="text-primary text-lg" />
                  <h3 className="text-sm font-semibold text-slate-900">Thông tin ca chụp</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-800">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-600">Modality</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                      {exam.modality || '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-slate-600">Trạng thái</span>
                    {getStatusBadge(exam.status)}
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-medium text-slate-600">Lịch hẹn</span>
                    <span className="text-right flex-1 ml-3">{formatDateTime(exam.scheduled_time)}</span>
                  </div>
                  {exam.study_date && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Ngày chụp</span>
                      <span className="text-right flex-1 ml-3">
                        {exam.study_date} {exam.study_time ? exam.study_time.substring(0, 5) : ''}
                      </span>
                    </div>
                  )}
                  {exam.completed_at && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Hoàn thành</span>
                      <span className="text-right flex-1 ml-3">{formatDateTime(exam.completed_at)}</span>
                    </div>
                  )}
                  {exam.site_name && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Cơ sở</span>
                      <span className="text-right flex-1 ml-3">{exam.site_name}</span>
                    </div>
                  )}
                  {exam.station_name && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Máy XQ</span>
                      <span className="text-right flex-1 ml-3">
                        {exam.station_name}
                        {exam.station_ae_title && (
                          <code className="text-xs bg-slate-100 text-slate-700 rounded px-1 py-0.5 ml-1">
                            {exam.station_ae_title}
                          </code>
                        )}
                      </span>
                    </div>
                  )}
                  {exam.external_id && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Mã chỉ định HIS</span>
                      <span className="text-right flex-1 ml-3">{exam.external_id}</span>
                    </div>
                  )}
                  {exam.accession_number && (
                    <div className="flex justify-between items-start">
                      <span className="text-xs font-medium text-slate-600">Accession Number</span>
                      <span className="text-right flex-1 ml-3">{exam.accession_number}</span>
                    </div>
                  )}
                  {exam.study_instance_uid && (
                    <div className="md:col-span-2">
                      <span className="block text-xs font-medium text-slate-600 mb-1">Study Instance UID</span>
                      <code className="text-xs bg-slate-100 text-slate-700 rounded px-2 py-1 break-all">
                        {exam.study_instance_uid}
                      </code>
                    </div>
                  )}
                </div>
              </div>

              {/* Thông tin chỉ định */}
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                  <FiFileText className="text-primary text-lg" />
                  <h3 className="text-sm font-semibold text-slate-900">Thông tin chỉ định</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-800">
                  {exam.description && (
                    <div className="md:col-span-2">
                      <span className="block text-xs font-medium text-slate-600 mb-1">Mô tả</span>
                      <span className="text-sm text-slate-900">{exam.description}</span>
                    </div>
                  )}
                  {exam.reason_for_study && (
                    <div className="md:col-span-2">
                      <span className="block text-xs font-medium text-slate-600 mb-1">Lý do chỉ định</span>
                      <span className="text-sm text-slate-900">{exam.reason_for_study}</span>
                    </div>
                  )}
                  {exam.clinical_info && (
                    <div className="md:col-span-2">
                      <span className="block text-xs font-medium text-slate-600 mb-1">Thông tin lâm sàng</span>
                      <span className="text-sm text-slate-900 whitespace-pre-wrap">{exam.clinical_info}</span>
                    </div>
                  )}
                  {exam.procedure_code && (
                    <div>
                      <span className="block text-xs font-medium text-slate-600 mb-1">Mã thủ thuật</span>
                      <span className="text-sm text-slate-900 bg-slate-100 px-2 py-1 rounded">
                        {exam.procedure_code}
                      </span>
                    </div>
                  )}
                  {exam.procedure_description && (
                    <div className="md:col-span-2">
                      <span className="block text-xs font-medium text-slate-600 mb-1">Mô tả thủ thuật</span>
                      <span className="text-sm text-slate-900">{exam.procedure_description}</span>
                    </div>
                  )}
                  {exam.service_id && (
                    <div>
                      <span className="block text-xs font-medium text-slate-600 mb-1">Mã dịch vụ</span>
                      <span className="text-sm text-slate-900">{exam.service_id}</span>
                    </div>
                  )}
                  {exam.service_name && (
                    <div>
                      <span className="block text-xs font-medium text-slate-600 mb-1">Tên dịch vụ</span>
                      <span className="text-sm text-slate-900">{exam.service_name}</span>
                    </div>
                  )}
                  {exam.order_priority && (
                    <div>
                      <span className="block text-xs font-medium text-slate-600 mb-1">Độ ưu tiên</span>
                      <span className="text-sm text-slate-900">
                        {exam.order_priority === 'R'
                          ? 'Routine'
                          : exam.order_priority === 'S'
                          ? 'Stat (Cấp cứu)'
                          : exam.order_priority === 'A'
                          ? 'ASAP'
                          : exam.order_priority === 'P'
                          ? 'Preop'
                          : exam.order_priority === 'C'
                          ? 'Callback'
                          : exam.order_priority}
                      </span>
                    </div>
                  )}
                  {exam.order_notes && (
                    <div className="md:col-span-2">
                      <span className="block text-xs font-medium text-slate-600 mb-1">Ghi chú</span>
                      <span className="text-sm text-slate-900 whitespace-pre-wrap">{exam.order_notes}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Thông tin kết quả */}
              {(exam.result_conclusion || exam.result_recommendation) && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                    <FiFileText className="text-primary text-lg" />
                    <h3 className="text-sm font-semibold text-slate-900">Kết luận</h3>
                  </div>
                  <div className="space-y-3 text-sm text-slate-800">
                    {exam.result_conclusion && (
                      <div>
                        <span className="block text-xs font-medium text-slate-600 mb-1"></span>
                        <span className="text-sm text-slate-900 whitespace-pre-wrap bg-slate-50 p-2 rounded border border-slate-200">{exam.result_conclusion}</span>
                      </div>
                    )}
                    {exam.result_recommendation && (
                      <div>
                        <span className="block text-xs font-medium text-slate-600 mb-1">Khuyến cáo</span>
                        <span className="text-sm text-slate-900 whitespace-pre-wrap bg-slate-50 p-2 rounded border border-slate-200">{exam.result_recommendation}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Thông tin bác sĩ */}
              {(exam.referring_physician_name || exam.requesting_physician_name) && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
                    <FiUserCheck className="text-primary text-lg" />
                    <h3 className="text-sm font-semibold text-slate-900">Thông tin bác sĩ</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-800">
                    {exam.referring_physician_name && (
                      <div className="md:col-span-2 flex justify-between items-start">
                        <span className="text-xs font-medium text-slate-600">Bác sĩ chỉ định</span>
                        <span className="text-right flex-1 ml-3">
                          {exam.referring_physician_name}
                          {exam.referring_physician_id && (
                            <span className="text-slate-500"> ({exam.referring_physician_id})</span>
                          )}
                        </span>
                      </div>
                    )}
                    {exam.requesting_physician_name && (
                      <div className="md:col-span-2 flex justify-between items-start">
                        <span className="text-xs font-medium text-slate-600">Bác sĩ yêu cầu</span>
                        <span className="text-right flex-1 ml-3">
                          {exam.requesting_physician_name}
                          {exam.requesting_physician_id && (
                            <span className="text-slate-500"> ({exam.requesting_physician_id})</span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamDetailModal;


